import React, { useEffect, useState } from 'react';
import { format, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { useAgentContext } from '../contexts/AgentContext';
import { Appointment } from '../types/index';
import useSWR from 'swr';
import { fetcher } from '../lib/api';

const Dashboard: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const { user, preferences } = useAgentContext();

  const { data, error, isLoading } = useSWR(
    `${import.meta.env.VITE_BACKEND_URL}/api/calendar/today`,
    fetcher,
  );

  useEffect(() => {
    if (data) {
      setAppointments(data);
    }
  }, [data]);

  const truncateDescription = (description: string, maxLength: number = 150) => {
    if (description.length <= maxLength) return description;
    return `${description.slice(0, maxLength)}...`;
  };

  const isTrialExpired = () => {
    if (!user?.trialExpiry) return false;
    const trialExpiry = new Date(user.trialExpiry);
    const today = new Date();
    return differenceInDays(trialExpiry, today) < 0;
  };

  const isActiveSubscription = () => {
    if (
      user?.subscriptionPlan !== 'free' &&
      user?.subscriptionPeriodEnd &&
      new Date(user.subscriptionPeriodEnd) > new Date()
    )
      return true;
    return false;
  };

  const getTrialBanner = () => {
    if (!user?.trialExpiry) return null;
    if (isActiveSubscription()) return null;

    const trialExpiry = new Date(user.trialExpiry);
    const today = new Date();
    const daysRemaining = differenceInDays(trialExpiry, today);
    const isTrial = user?.subscriptionPlan === 'free';

    let title = `Tu ${isTrial ? 'período de prueba' : 'suscripción'} está por expirar`;
    let description: React.ReactNode = `Te quedan ${daysRemaining} día${daysRemaining !== 1 ? 's' : ''} de prueba.`;

    if (daysRemaining < 0) {
      title = `Tu ${isTrial ? 'período de prueba' : 'suscripción'} ha expirado`;
      description = (
        <React.Fragment>
          Para continuar usando el servicio, por favor{' '}
          <Link to="/subscription" className="text-secondary underline">
            contrata un plan
          </Link>
          .
        </React.Fragment>
      );
    }

    if (daysRemaining <= 7) {
      return (
        <div className="bg-[#FFF8E5] border-l-4 border-[#FFE5B2] text-[#CC9944] p-4 mb-6">
          <p className="font-bold">{title}</p>
          <p className="opacity-80">{description}</p>
        </div>
      );
    }

    return null;
  };

  const getPreferencesBanner = () => {
    if (!preferences || !preferences.timezone || !preferences.workingHours) {
      return (
        <div className="bg-[#FFF8E5] border-l-4 border-[#FFE5B2] text-[#CC9944] p-4 mb-6">
          <div className="space-y-4">
            <div>
              <p className="font-bold">¡Configura tus preferencias!</p>
              <p className="opacity-80">
                Para una mejor experiencia, por favor configura tus preferencias de horario y zona
                horaria.
              </p>
            </div>
            <div>
              <Link
                to="/preferences"
                className="px-6 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors whitespace-nowrap"
              >
                Configurar ⚙️
              </Link>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!user?.id) {
    return null; // Don't render anything while redirecting
  }

  const trialExpired = isTrialExpired();
  const hasActiveSubscription = isActiveSubscription();
  const hasPreferences = preferences && preferences.timezone && preferences.workingHours;
  
  // Buttons should be enabled if user has preferences AND either trial is not expired OR has active subscription
  const buttonsEnabled = hasPreferences && (!trialExpired || hasActiveSubscription);
  
  const disabledButtonClass =
    'block w-full py-2 px-4 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed';
  const activeButtonClass =
    'block w-full py-2 px-4 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors text-center';

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {getTrialBanner()}
        {getPreferencesBanner()}
        <h1 className="text-3xl font-bold text-primary mb-4">Inicio</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Calendar Overview */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-primary mb-4">Agenda de Hoy</h2>
            <div className="space-y-4 overflow-hidden">
              {isLoading ? (
                <p className="text-gray-600">Cargando citas...</p>
              ) : error ? (
                <p className="text-red-500">{'Error cargando citas: ' + error.message}</p>
              ) : appointments.length === 0 ? (
                <p className="text-gray-600">No hay reuniones programadas para hoy</p>
              ) : (
                appointments.map((appointment) => (
                  <div key={appointment.id} className="border-l-4 border-secondary pl-4 py-2">
                    <h3 className="font-semibold text-primary break-words">
                      {appointment.summary}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {format(new Date(appointment.start), 'HH:mm', { locale: es })} -&nbsp;
                      {format(new Date(appointment.end), 'HH:mm', { locale: es })}
                    </p>
                    {appointment.description && (
                      <div className="text-sm text-gray-500 mt-1 break-words whitespace-pre-wrap overflow-x-auto max-w-full">
                        {truncateDescription(appointment.description)}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-primary mb-4">Acciones Rápidas</h2>
            <div className="space-y-4">
              <a
                href="https://calendar.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className={activeButtonClass}
              >
                Ver Calendario 📅
              </a>
              {buttonsEnabled ? (
                <>
                  <Link to="/chat" className={activeButtonClass}>
                    Probar Chat 💬
                  </Link>
                  <Link to="/voice" className={activeButtonClass}>
                    Probar Llamada 📞
                  </Link>
                </>
              ) : 
              <>
              <button
                disabled
                className={disabledButtonClass}
                title={
                  !hasPreferences ? 'Configura tus preferencias primero' : 'Prueba expirada'
                }
              >
                Probar Chat 💬
              </button>
              <button
                disabled
                className={disabledButtonClass}
                title={
                  !hasPreferences ? 'Configura tus preferencias primero' : 'Prueba expirada'
                }
              >
                Probar Llamada 📞
              </button>
            </>
            
            }
              
              <Link to="/preferences" className={activeButtonClass}>
                Actualizar Preferencias ⚙️
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
